import type { ComponentType, ReactNode } from 'react';
import Select, { components as rsComponents } from 'react-select';
import type { CellReactSelectProps, SelectOption } from '../types';

const IndicatorsContainer = rsComponents.IndicatorsContainer as unknown as ComponentType<{ children?: ReactNode }>;
const RSDropdownIndicator = rsComponents.DropdownIndicator as unknown as ComponentType<React.PropsWithChildren<Record<string, unknown>>>;

/** Replace react-select's default SVG chevron with the Bombiix icon-font glyph (matches NewSelectMultiSelect). */
function BombiixDropdownIndicator(props: React.PropsWithChildren<Record<string, unknown>>) {
  return (
    <RSDropdownIndicator {...props}>
      <i className="bombiix-icon-chevron-down" aria-hidden="true" />
    </RSDropdownIndicator>
  );
}

/* Master-design tokens (mirror NewSelectMultiSelect so grid selects match the PIM look). */
const FONT_BODY = "'Aktiv Grotesk', sans-serif";
const FONT_MEDIUM = "'Aktiv Grotesk', sans-serif";
const COLOR_TEXT = '#3c3c3c';
const COLOR_PLACEHOLDER = '#a4a4a4';
const BORDER = '#d8d8d8';
const ERROR_BORDER = '#fa979b';
const COLOR_OPTION_HOVER = '#ceeafc';
const COLOR_CHIP_BG = '#c2e5fb';
const COLOR_CHIP_REMOVE_HOVER = '#6dc1f5';
const ALERT_BG = '#fee5e6';
const MUTED = 'rgba(106, 106, 106, 0.75)';

/** Pink "required field" alert badge shown inside the control's indicator area on error. */
function AlertIndicators(props: { children?: ReactNode }) {
  return (
    <IndicatorsContainer {...props}>
      <span
        aria-hidden
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          borderRadius: 4,
          marginRight: 4,
          flex: '0 0 auto',
          backgroundColor: ALERT_BG,
        }}
      >
        <i className="icon-Alert-Triangle" />
      </span>
      {props.children}
    </IndicatorsContainer>
  );
}

/** react-select styled to match the Figma spec for grid controls: 28px tall, 4px radius, #D8D8D8 border, 13px. */
const buildStyles = (bare: boolean, error: boolean, fontSize: number) => ({
  container: (base: Record<string, unknown>) => ({ ...base, width: '100%' }),
  control: (base: Record<string, unknown>) => {
    const restingBorder = error ? ERROR_BORDER : BORDER;
    return {
      ...base,
      minHeight: 28,
      height: 28,
      borderRadius: 4,
      border: bare ? 'none' : `1px solid ${restingBorder}`,
      boxShadow: 'none',
      backgroundColor: bare ? 'transparent' : '#fff',
      fontFamily: FONT_BODY,
      fontSize: 14,
      cursor: 'pointer',
      '&:hover': { borderColor: bare ? 'transparent' : restingBorder },
    };
  },
  valueContainer: (base: Record<string, unknown>) => ({ ...base, padding: '0 0 0 10px', height: 26 }),
  singleValue: (base: Record<string, unknown>) => ({ ...base, color: COLOR_TEXT, fontFamily: FONT_BODY, fontSize: 14 }),
  placeholder: (base: Record<string, unknown>) => ({
    ...base,
    color: error ? COLOR_TEXT : COLOR_PLACEHOLDER,
    fontFamily: FONT_BODY,
    fontSize: 14,
  }),
  input: (base: Record<string, unknown>) => ({ ...base, color: COLOR_TEXT, fontFamily: FONT_BODY, fontSize: 14, margin: 0, padding: 0 }),
  indicatorsContainer: (base: Record<string, unknown>) => ({ ...base, height: 26, paddingRight: 12 }),
  dropdownIndicator: (base: Record<string, unknown>) => ({
    ...base,
    padding: 0,
    color: COLOR_PLACEHOLDER,
    MUTED,
    fontSize: 16,
    '&:hover': { color: COLOR_TEXT },
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  multiValue: (base: Record<string, unknown>) => ({
    ...base,
    backgroundColor: COLOR_CHIP_BG,
    borderRadius: 4,
    margin: '2px 4px 2px 0',
    overflow: 'hidden',
  }),
  multiValueLabel: (base: Record<string, unknown>) => ({
    ...base,
    color: COLOR_TEXT,
    fontFamily: FONT_MEDIUM,
    fontSize: 12,
    fontWeight: 500,
    padding: '2px 4px 2px 6px',
    lineHeight: '16px',
  }),
  multiValueRemove: (base: Record<string, unknown>) => ({
    ...base,
    color: COLOR_TEXT,
    padding: '0 6px',
    cursor: 'pointer',
    fontSize: 12,
    borderRadius: '0 4px 4px 0',
    '&:hover': { backgroundColor: COLOR_CHIP_REMOVE_HOVER, color: '#fff' },
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    fontSize,
    borderRadius: 8,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.25)',
    marginTop: 4,
    border: 'none',
    overflow: 'hidden',
    width: 'max-content',
    minWidth: '100%',
    maxWidth: 240,
  }),
  menuList: (base: Record<string, unknown>) => ({
    ...base,
    padding: 6,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  }),
  option: (base: Record<string, unknown>, state: { isFocused: boolean; isSelected: boolean }) => ({
    ...base,
    minHeight: 24,
    padding: '0 6px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    borderRadius: 4,
    cursor: 'pointer',
    color: COLOR_TEXT,
    fontFamily: FONT_MEDIUM,
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    backgroundColor: state.isFocused || state.isSelected ? COLOR_OPTION_HOVER : 'transparent',
    '&:active': { backgroundColor: COLOR_OPTION_HOVER },
  }),
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
});

/** Single-value react-select for grid cells (string value in/out, portaled menu). */
export default function CellReactSelect({
  value,
  options,
  onChange,
  placeholder,
  bare = false,
  error = false,
  disabled = false,
  fontSize = 14,
  isClearable = false,
}: CellReactSelectProps) {
  const selected = options.find((o) => o.value === value) ?? null;
  return (
    <Select
      options={options}
      value={selected}
      onChange={(opt: SelectOption | null) => onChange(opt ? opt.value : '')}
      placeholder={placeholder}
      classNamePrefix="mssg-rs"
      isSearchable={false}
      isClearable={isClearable}
      isDisabled={disabled}
      styles={buildStyles(bare, error, fontSize)}
      components={
        error ? { DropdownIndicator: BombiixDropdownIndicator, IndicatorsContainer: AlertIndicators } : { DropdownIndicator: BombiixDropdownIndicator }
      }
      menuPlacement="auto"
      menuPosition="fixed"
      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
    />
  );
}
